import type { FormikErrors, FormikProps } from 'formik'

import { FlowPage } from '@monthio/design-system'
import {
	FlowHeader,
	FormInput,
	FormMultipleChoice,
	makeStyles,
	Section,
} from '@monthio/react-components'
import i18n from 'i18next'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	DefaultConsumerInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '../types/questionnaire.types'

import { ScrollToViewContainer } from '../../../modulesTemp/shared/ScrollToViewContainer'
import {
	ChildrenAgeGroup,
	CivilStatus,
	HousingStatus,
} from '../types/questionnaire.types'
import { FormikNumberInput } from './formikIntegration'
import { FormikSingleSelect } from './formikIntegration/singleSelect'
import { InputSection } from './shared'

const civilStatusChoices = () => [
	{
		value: CivilStatus.Married,
		label: i18n.t(
			'consumerInputs-familyInformation-semlerMarriedOrCohabiting-label',
		),
	},
	{
		value: CivilStatus.Alone,
		label: i18n.t('consumerInputs-familyInformation-semlerSingle-label'),
	},
]

const getHousingChoices = () => {
	return [
		{
			value: HousingStatus.OwnerOccupied,
			label: i18n.t(
				'consumerInputs-familyInformation-housingStatus-ownerHousing',
			),
		},
		{
			value: HousingStatus.HousingCooperative,
			label: i18n.t(
				'consumerInputs-familyInformation-housingStatus-housingCooperative',
			),
		},
		{
			value: HousingStatus.Rent,
			label: i18n.t(
				'consumerInputs-familyInformation-housingStatus-rentalProperty',
			),
		},
		{
			value: HousingStatus.LivingAtHome,
			label: i18n.t(
				'consumerInputs-familyInformation-housingStatus-livingAtHome',
			),
		},
		{
			value: HousingStatus.Other,
			label: i18n.t('consumerInputs-familyInformation-housingStatus-other'),
		},
	]
}

const FamilyInformationStepComponent = ({
	formProps,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
}) => FamilyInformationBaseStepComponent({ formProps })

const FamilyInformationBaseStepComponent = ({
	formProps,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
}) => {
	const { t } = useTranslation()

	const updateChildrenAges = useCallback(
		(newChildrenCount: number | undefined) => {
			const childrenCount = newChildrenCount ?? 0
			const currentNumberOfChildren =
				formProps.values.familyInformation?.childrenAges.length ?? 0

			if (childrenCount < currentNumberOfChildren) {
				formProps.setFieldValue(
					'familyInformation.childrenAges',
					formProps.values.familyInformation?.childrenAges.slice(
						0,
						newChildrenCount,
					),
					true,
				)
			} else {
				const nrToAdd = childrenCount - currentNumberOfChildren

				const toAdd = []
				for (let i = 0; i < nrToAdd; i++) {
					toAdd[i] = ChildrenAgeGroup.Unknown
				}
				formProps.setFieldValue(
					'familyInformation.childrenAges',
					formProps.values.familyInformation?.childrenAges.concat(toAdd),
					true,
				)
			}
		},
		[formProps],
	)

	useEffect(() => {
		formProps.setSubmitting(false)
		formProps.setTouched({
			...formProps.touched,
			familyInformation: undefined,
		})
	}, [])

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-familyInformation-personalInformation-title')}
				description={t(
					'consumerInputs-familyInformation-personalInformation-text',
				)}
			/>
			<ScrollToViewContainer>
				<FormikSingleSelect
					fieldName="familyInformation.civilStatus"
					label={t('consumerInputs-familyInformation-civilStatusChoice-title')}
					isRequired
					choices={civilStatusChoices()}
				/>
			</ScrollToViewContainer>

			<ScrollToViewContainer timeout={100}>
				<FormikNumberInput
					fieldName="familyInformation.childrenCount"
					label={t('consumerInputs-familyInformation-numberOfChildren-text')}
					isRequired
					additionalOnChange={updateChildrenAges}
				/>
			</ScrollToViewContainer>

			{ChildrenAgeInput(formProps)}

			<ScrollToViewContainer>
				<FormikSingleSelect
					fieldName="familyInformation.housingStatus"
					label={t('consumerInputs-familyInformation-housingStatus-title')}
					isRequired
					choices={getHousingChoices()}
				/>
			</ScrollToViewContainer>
		</FlowPage.Content>
	)
}

const FamilyInformationStepValidation = async ({
	values,
	errors,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	if (
		!values.familyInformation?.civilStatus ||
		values.familyInformation.civilStatus == CivilStatus.None
	) {
		errors.familyInformation = {
			...errors.familyInformation,
			civilStatus: i18n.t('validation-fieldRequired'),
		}
	}

	if (values.familyInformation?.childrenCount == null) {
		errors.familyInformation = {
			...errors.familyInformation,
			childrenCount: i18n.t('validation-fieldRequired'),
		}
	}

	if (!values.familyInformation?.housingStatus) {
		errors.familyInformation = {
			...errors.familyInformation,
			housingStatus: i18n.t('validation-fieldRequired'),
		}
	}

	if (
		(values.familyInformation?.childrenAges?.length ?? 0) > 0 &&
		values.familyInformation?.childrenAges.includes(ChildrenAgeGroup.Unknown)
	) {
		errors.familyInformation = {
			...errors.familyInformation,
			childrenAges: i18n.t(
				'consumerInputs-familyInformation-childrenAges-error',
			),
		}
	}

	return errors
}

const ChildrenAgeInput = (formProps: FormikProps<DefaultConsumerInputs>) => {
	const { t } = useTranslation()

	if ((formProps.values.familyInformation?.childrenCount ?? 0) > 0) {
		return (
			<InputSection>
				{formProps.values.familyInformation?.childrenAges.map(
					(_: any, i: number) => (
						<ScrollToViewContainer key={`ChildrenAge::${i}`}>
							<FormikSingleSelect
								className={'scroll-item-to-view'}
								fieldName={`familyInformation.childrenAges[${i}]`}
								label={`${t(
									'consumerInputs-familyInformation-childrenAge-text',
								)}  ${i + 1}`}
								// Hack to get around the fact that the 'validation error' is set on 'familyInformation.childrenAges' in the validation.
								// but the field name used to get the error text in the 'formik integration' is 'familyInformation.childrenAges[i]'.
								// Can be removed when backwards compatibility with react-components component MultipleChoice is no longer needed.
								errorText={i18n.t(
									'consumerInputs-familyInformation-childrenAges-error',
								)}
								isRequired
								choices={[
									{
										value: ChildrenAgeGroup.AgeGroup0To1,
										label: t(
											'consumerInputs-familyInformation-childrenAges-option1',
										),
									},
									{
										value: ChildrenAgeGroup.AgeGroup2To6,
										label: t(
											'consumerInputs-familyInformation-childrenAges-option2',
										),
									},
									{
										value: ChildrenAgeGroup.AgeGroup7To17,
										label: t(
											'consumerInputs-familyInformation-childrenAges-option3',
										),
									},
									{
										value: ChildrenAgeGroup.AgeGroupOver18,
										label: t(
											'consumerInputs-familyInformation-childrenAges-option4',
										),
									},
								]}
							/>
						</ScrollToViewContainer>
					),
				)}
			</InputSection>
		)
	}
}

export const FamilyInformationStep: QuestionnaireStep = {
	validation: FamilyInformationStepValidation,
	component: FamilyInformationStepComponent,
}
